import { useEffect, useMemo, useRef, useState } from "react";
import { fetchBlobText } from "../services/sasService";

/** First path segment after /archive/ (per-model package id, usually a UUID). */
function getModelIdAfterArchive(pathOnly) {
  if (!pathOnly) return "";
  const marker = "/archive/";
  const idx = pathOnly.indexOf(marker);
  if (idx === -1) return "";
  const after = pathOnly.slice(idx + marker.length);
  return after.split("/")[0] || "";
}

const EXPECTED_TABS = [
  {
    order: 1,
    filename: "1. Model_Data_Summary.md",
    name: "Model Data Summary",
  },
  { order: 2, filename: "2. Model_Outputs.md", name: "Model Outputs" },
  { order: 3, filename: "3. Insights.md", name: "Insights" },
];

const AUTH_SUMMARY_ERROR = "Please sign in again to view AI summaries.";

function isAuthHttpStatus(status) {
  return status === 401 || status === 403;
}

function orderedTabsFromProbe(byOrder) {
  return EXPECTED_TABS.map((tab) => byOrder.get(tab.order)).filter(Boolean);
}

async function fetchSummaryTab(summariesPath, tab) {
  const blobPath = `${summariesPath}${tab.filename}`;
  try {
    const { content: text } = await fetchBlobText(blobPath);
    return { ...tab, path: blobPath, content: text };
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return null;
    if (isAuthHttpStatus(status) || /signed in/i.test(err?.message || "")) {
      throw new Error(AUTH_SUMMARY_ERROR, { cause: err });
    }
    if (status) {
      // Other HTTP failures (e.g. 500): skip this tab, keep probing others.
      console.warn("AI summary fetch failed for", blobPath, err);
      return null;
    }
    // Non-HTTP failures bubble so the probe can surface listError.
    throw err;
  }
}

/**
 * Data layer for the AI Summary feature: derives the summaries path and
 * fetches markdown via DMQT API blob-text proxy (avoids Azure CORS).
 */
function useAISummaryData(guuid, folder) {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const contentCacheRef = useRef({});

  const summariesPath = useMemo(() => {
    if (!folder?.path) return "";
    const pathOnly = folder.path.split("|")[0];
    if (!pathOnly.includes("/archive/")) return "";
    const beforeArchive = pathOnly.split("/archive/")[0];
    const fromArchive = getModelIdAfterArchive(pathOnly);
    const modelId = fromArchive || (guuid && String(guuid).trim()) || "";
    if (!beforeArchive || !modelId) return "";
    return `${beforeArchive}/summaries/${modelId}/`;
  }, [folder?.path, guuid]);

  // Probe expected .md files in parallel via API proxy.
  useEffect(() => {
    contentCacheRef.current = {};
    if (!summariesPath) {
      setTabs([]);
      setContent("");
      setListLoading(false);
      setListError(null);
      return undefined;
    }

    let cancelled = false;
    let firstPaintDone = false;
    const byOrder = new Map();

    const applyProbeProgress = () => {
      if (cancelled) return;
      const found = orderedTabsFromProbe(byOrder);
      setTabs(found);
      if (!firstPaintDone && found.length > 0) {
        firstPaintDone = true;
        setActiveTab(0);
        setContent(found[0].content);
        setListLoading(false);
      }
    };

    setListLoading(true);
    setListError(null);
    setTabs([]);
    setContent("");
    setActiveTab(0);

    Promise.allSettled(
      EXPECTED_TABS.map((tab) =>
        fetchSummaryTab(summariesPath, tab).then((loaded) => {
          if (cancelled || !loaded) return;
          byOrder.set(loaded.order, loaded);
          contentCacheRef.current[loaded.filename] = loaded.content;
          applyProbeProgress();
        }),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        // Only surface probe failures when nothing loaded (avoid hiding good tabs).
        if (byOrder.size > 0) return;
        const rejection = results.find((r) => r.status === "rejected");
        if (rejection) {
          setListError(rejection.reason?.message || "Failed to load summaries");
        }
      })
      .finally(() => {
        if (cancelled) return;
        setListLoading(false);
        if (!firstPaintDone) {
          setTabs([]);
          setContent("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [summariesPath]);

  // Switch tab: use probe/refetch cache (or tab.content) before fetching again.
  useEffect(() => {
    if (!tabs.length || activeTab < 0 || activeTab >= tabs.length) return;
    const tab = tabs[activeTab];
    const cached = contentCacheRef.current[tab.filename] ?? tab.content;
    if (cached !== undefined) {
      contentCacheRef.current[tab.filename] = cached;
      setContent(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchBlobText(`${summariesPath}${tab.filename}`)
      .then(({ content: text }) => {
        contentCacheRef.current[tab.filename] = text;
        if (!cancelled) setContent(text);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (isAuthHttpStatus(status) || /signed in/i.test(err?.message || "")) {
          setContent(AUTH_SUMMARY_ERROR);
        } else {
          setContent("Could not load summary.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, tabs, summariesPath]);

  return {
    tabs,
    activeTab,
    setActiveTab,
    content,
    loading,
    listLoading,
    listError,
    fileList: tabs.map((t) => t.path || `${summariesPath}${t.filename}`),
    summariesPath,
    hasPath: Boolean(folder?.path && summariesPath),
  };
}

export default useAISummaryData;
